import React, { useEffect, useState } from 'react'
import { Grid, Box, Paper, Table, TableBody, TableCell, TableRow, TableHead, TableContainer, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Checkbox, FormControlLabel, List, ListItem, ListItemText, CircularProgress, Tabs, Tab, Chip } from "@mui/material"
import * as actions from "./actions"
import moment from "moment"
import Pagination from '@mui/material/Pagination';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { toastError, toastSuccess } from '../../utils';

export default function LabelRequests(props) {

    const [requests, setRequests] = useState([])
    const [activeTab, setActiveTab] = useState(0) // 0 = In Review, 1 = History
    const [filters, setFilters] = useState({
        limit: 20,
        offset: 0,
        page: 1,
        status: 'in_review'
    })
    const [count, setCount] = useState(0)
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        action: null,
        reviewId: null
    })
    const [assignModal, setAssignModal] = useState({
        open: false,
        userId: null
    })
    const [subAdmins, setSubAdmins] = useState([])
    const [selectedSubAdmins, setSelectedSubAdmins] = useState([])
    const [assignLoading, setAssignLoading] = useState(false)
    const [deleteModal, setDeleteModal] = useState({
        open: false,
        reviewId: null
    })

    useEffect(() => {
        onGetLabelRequests(filters)
    }, [filters])

    const onPageChange = (event, page) => {
        const offset = (page - 1) * filters.limit
        setFilters({ ...filters, page, offset })
    }

    const onTabChange = (event, newValue) => {
        setActiveTab(newValue)
        // Reset pagination and set status based on tab
        const status = newValue === 0 ? 'in_review' : 'approved,rejected'
        setFilters({ limit: 20, offset: 0, page: 1, status })
    }

    const onGetLabelRequests = (filters) => {
        props.setLoader(true)
        actions.showLabelRequestsInReview(filters).then(res => {
            props.setLoader(false)
            setRequests(res.body || [])
            setCount(res.total || 0)
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to load label requests');
        })
    }

    const openConfirmModal = (reviewId, action) => {
        setConfirmModal({
            open: true,
            action,
            reviewId
        })
    }

    const closeConfirmModal = () => {
        setConfirmModal({
            open: false,
            action: null,
            reviewId: null
        })
    }

    const onActionRequest = () => {
        const { reviewId, action } = confirmModal
        closeConfirmModal()
        props.setLoader(true)
        actions.actionLabelRequestAdmin({ review_id: reviewId, action }).then(res => {
            props.setLoader(false)
            toastSuccess(res.message || `Request ${action} successfully`);

            // If approved, show assignment modal
            if (action === 'approved' && res.body?.user_id) {
                fetchSubAdminsAndOpenModal(res.body.user_id)
            } else {
                onGetLabelRequests(filters)
            }
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || `Failed to ${action} request`);
        })
    }

    const fetchSubAdminsAndOpenModal = (userId) => {
        setAssignLoading(true)
        actions.getSubAdminsListAdmin().then(res => {
            setAssignLoading(false)
            setSubAdmins(res.body || [])
            setSelectedSubAdmins([])
            setAssignModal({ open: true, userId })
        }).catch(err => {
            setAssignLoading(false)
            toastError(err?.message || 'Failed to fetch sub_admins');
            onGetLabelRequests(filters)
        })
    }

    const handleSubAdminToggle = (subAdminId) => {
        setSelectedSubAdmins(prev => {
            if (prev.includes(subAdminId)) {
                return prev.filter(id => id !== subAdminId)
            } else {
                return [...prev, subAdminId]
            }
        })
    }

    const handleAssignSubmit = () => {
        if (selectedSubAdmins.length === 0) {
            toastError('Please select at least one sub_admin');
            return;
        }

        props.setLoader(true)
        actions.assignUserToSubAdmins({
            user_id: assignModal.userId,
            subAdminIds: selectedSubAdmins
        }).then(res => {
            props.setLoader(false)
            toastSuccess(res.message || 'User assigned successfully');
            closeAssignModal()
            onGetLabelRequests(filters)
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to assign user');
        })
    }

    const handleSkipAssign = () => {
        closeAssignModal()
        onGetLabelRequests(filters)
    }

    const closeAssignModal = () => {
        setAssignModal({ open: false, userId: null })
        setSelectedSubAdmins([])
    }

    const openDeleteModal = (reviewId) => {
        setDeleteModal({ open: true, reviewId })
    }

    const closeDeleteModal = () => {
        setDeleteModal({ open: false, reviewId: null })
    }

    const onDeleteB2BFile = () => {
        const { reviewId } = deleteModal
        closeDeleteModal()
        props.setLoader(true)
        actions.deleteB2BFileAdmin({ review_id: reviewId }).then(res => {
            props.setLoader(false)
            toastSuccess(res.message || 'B2B file deleted successfully')
            onGetLabelRequests(filters)
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to delete B2B file')
        })
    }

    let totalPages = Math.ceil(Number(count) / filters.limit)

    const isHistoryTab = activeTab === 1

    return (
        <Grid container>
            <Grid item xs={12}>
                <Box mb={2} display="flex" justifyContent="space-between">
                    <Grid container>
                        <Grid item xs={12} sm={6}>
                            <h2 className="">New Label Requests</h2>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <div>
                                <h2 style={{ float: "right" }}>Total: {count}</h2>
                            </div>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box mb={2}>
                    <Tabs
                        value={activeTab}
                        onChange={onTabChange}
                        sx={{
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 600,
                            },
                            '& .Mui-selected': {
                                color: '#12B4A1 !important',
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#12B4A1',
                            },
                        }}
                    >
                        <Tab label="In Review" />
                        <Tab label="History" />
                    </Tabs>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow style={{ background: "linear-gradient(90deg, rgba(19,153,121,1) 0%, rgba(18,180,161,1) 45%, rgba(5,150,179,1) 100%)" }}>
                                    <TableCell className="color-white">No.</TableCell>
                                    <TableCell className="color-white">Label Email</TableCell>
                                    <TableCell className="color-white">Name</TableCell>
                                    <TableCell className="color-white">Email</TableCell>
                                    <TableCell className="color-white">Phone</TableCell>
                                    <TableCell className="color-white">Submitted On</TableCell>
                                    <TableCell className="color-white">B2B File</TableCell>
                                    {isHistoryTab && (
                                        <TableCell className="color-white">Delete File</TableCell>
                                    )}
                                    {isHistoryTab ? (
                                        <TableCell className="color-white">Status</TableCell>
                                    ) : (
                                        <TableCell className="color-white">Actions</TableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {
                                    requests.length ? requests.map((item, key) => {
                                        return <TableRow key={key}>
                                            <TableCell>{((filters.page - 1) * filters.limit) + (key + 1)}</TableCell>
                                            <TableCell>{item.label_email || '-'}</TableCell>
                                            <TableCell>{item.name || '-'}</TableCell>
                                            <TableCell>{item.email || '-'}</TableCell>
                                            <TableCell>{item.phone || '-'}</TableCell>
                                            <TableCell>{item.created_at ? moment(item.created_at).format('DD/MM/YYYY') : '-'}</TableCell>
                                            <TableCell>
                                                {item.b2b_file ? (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<DownloadIcon />}
                                                        href={item.b2b_file.replace('/b2bFileUploads/', '/')}
                                                        target="_blank"
                                                        download
                                                        sx={{
                                                            background: "linear-gradient(90deg, rgba(19,153,121,1) 0%, rgba(18,180,161,1) 45%, rgba(5,150,179,1) 100%)",
                                                            textTransform: 'none'
                                                        }}
                                                    >
                                                        Download
                                                    </Button>
                                                ) : '-'}
                                            </TableCell>
                                            {isHistoryTab && (
                                                <TableCell>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<DeleteIcon />}
                                                        onClick={() => openDeleteModal(item.review_id)}
                                                        disabled={!item.b2b_file}
                                                        sx={{
                                                            textTransform: 'none',
                                                            backgroundColor: item.b2b_file ? '#d32f2f' : '#9e9e9e',
                                                            '&:hover': {
                                                                backgroundColor: item.b2b_file ? '#b71c1c' : '#9e9e9e',
                                                            },
                                                            '&.Mui-disabled': {
                                                                backgroundColor: '#424242',
                                                                color: '#9e9e9e',
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                </TableCell>
                                            )}
                                            {isHistoryTab ? (
                                                <TableCell>
                                                    <Chip
                                                        label={item.status === 'approved' ? 'Approved' : 'Rejected'}
                                                        color={item.status === 'approved' ? 'success' : 'error'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            ) : (
                                                <TableCell>
                                                    <Box display="flex" gap={1}>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="success"
                                                            startIcon={<CheckCircleIcon />}
                                                            onClick={() => openConfirmModal(item.review_id, 'approved')}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            Approve
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            color="error"
                                                            startIcon={<CancelIcon />}
                                                            onClick={() => openConfirmModal(item.review_id, 'rejected')}
                                                            sx={{ textTransform: 'none' }}
                                                        >
                                                            Reject
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    }) : <TableRow>
                                        <TableCell colSpan={isHistoryTab ? 9 : 8} align="center">
                                            {isHistoryTab ? 'No History Found' : 'No Label Requests Found'}
                                        </TableCell>
                                    </TableRow>
                                }
                            </TableBody>
                        </Table>
                        <Pagination count={totalPages} showFirstButton showLastButton onChange={onPageChange} page={filters.page || 0} />
                    </TableContainer>
                </Box>
            </Grid>

            <Dialog
                open={confirmModal.open}
                onClose={closeConfirmModal}
                aria-labelledby="confirm-dialog-title"
                aria-describedby="confirm-dialog-description"
            >
                <DialogTitle id="confirm-dialog-title">
                    {confirmModal.action === 'approved' ? 'Approve Label Request' : 'Reject Label Request'}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="confirm-dialog-description">
                        {confirmModal.action === 'approved'
                            ? `Are you sure you want to approve this label request? This will create a new user account and send login credentials to their email.`
                            : `Are you sure you want to reject this label request? This action cannot be undone.`
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmModal} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={onActionRequest}
                        color={confirmModal.action === 'approved' ? 'success' : 'error'}
                        variant="contained"
                        autoFocus
                    >
                        {confirmModal.action === 'approved' ? 'Approve' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assignment Modal */}
            <Dialog
                open={assignModal.open}
                disableEscapeKeyDown
                aria-labelledby="assign-dialog-title"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="assign-dialog-title">
                    Assign User to Sub-Admins
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Select sub-admins who should have access to this newly created user:
                    </DialogContentText>
                    {assignLoading ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={30} sx={{ color: '#12B4A1' }} />
                        </Box>
                    ) : (
                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {subAdmins.length > 0 ? subAdmins.map((admin) => (
                                <ListItem
                                    key={admin.subAdminId}
                                    dense
                                    sx={{ borderBottom: '1px solid #eee' }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedSubAdmins.includes(admin.subAdminId)}
                                                onChange={() => handleSubAdminToggle(admin.subAdminId)}
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: '#12B4A1',
                                                    },
                                                }}
                                            />
                                        }
                                        label={
                                            <ListItemText
                                                primary={admin.name}
                                                secondary={admin.email}
                                            />
                                        }
                                        sx={{ width: '100%', m: 0 }}
                                    />
                                </ListItem>
                            )) : (
                                <ListItem>
                                    <ListItemText primary="No sub-admins found" />
                                </ListItem>
                            )}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSkipAssign} color="inherit">
                        Skip
                    </Button>
                    <Button
                        onClick={handleAssignSubmit}
                        variant="contained"
                        disabled={selectedSubAdmins.length === 0}
                        sx={{
                            background: "linear-gradient(90deg, rgba(19,153,121,1) 0%, rgba(18,180,161,1) 45%, rgba(5,150,179,1) 100%)",
                        }}
                    >
                        Assign ({selectedSubAdmins.length})
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete B2B File Confirmation Modal */}
            <Dialog
                open={deleteModal.open}
                onClose={closeDeleteModal}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete B2B File
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this B2B file? This action cannot be undone and the file will be permanently removed from the server.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteModal} color="inherit">
                        Cancel
                    </Button>
                    <Button
                        onClick={onDeleteB2BFile}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    )
}
